import {
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  OnDestroy,
  inject,
} from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DashboardService } from '../../../dashboard/dashboard.service';
import { AdminService } from '../../../admin/admin.service';
import { Platform } from '@angular/cdk/platform';
import { saveAs } from '@progress/kendo-file-saver';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  AsyncPipe,
  CurrencyPipe,
  DecimalPipe,
  Location,
} from '@angular/common';
import { BookManagementService } from '../../book-management.service';
import { NgxLoadingModule } from 'ngx-loading';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { GridModule } from '@progress/kendo-angular-grid';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';

@Component({
  selector: 'app-top-policy-review',
  templateUrl: './top-policy-review.component.html',
  styleUrls: ['./top-policy-review.component.scss'],
  imports: [
    NgxLoadingModule,
    MatProgressSpinnerModule,
    MatIconModule,
    GridModule,
    CurrencyPipe,
    DecimalPipe,
    AsyncPipe,
    MatTooltipModule,
    MatButtonModule,
  ],
})
export class TopPolicyReviewComponent implements OnInit, OnDestroy {
  private admnSrvc = inject(AdminService);
  private bmSvc = inject(BookManagementService);
  private cdRef = inject(ChangeDetectorRef);
  private dbs = inject(DashboardService);
  private location = inject(Location);
  private pltfrm = inject(Platform);
  private snacky = inject(MatSnackBar);

  @Input() title;
  @Input() configType;
  @Input() reportName;
  private baseUrl: string;
  unsubscribe: Subject<any> = new Subject();
  private widgetKey = this.bmSvc.nextWidgetKey('top-policy-review');
  loading$ = new BehaviorSubject<boolean>(false);
  get loading(): boolean {
    return this.loading$.value;
  }
  set loading(value: boolean) {
    if (this.loading$.value === value) {
      return;
    }
    this.loading$.next(value);
    this.bmSvc.setWidgetLoading(this.widgetKey, value);
  }
  fileLoading = false;
  skip = 0;
  take = 10;
  pageSizeOptions = [10, 25, 50, 75, 100];
  total = 0;
  gridData = { headers: [], data: [], total: 0 };
  filterValues = {};

  ngOnInit(): void {
    this.bmSvc.updateBmWidgets$
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(filterVals => {
        this.filterValues = filterVals;
        this.getUiConfig();
      });
  }

  ngOnDestroy() {
    this.bmSvc.setWidgetLoading(this.widgetKey, false);
    this.unsubscribe.next(null);
    this.unsubscribe.complete();
  }

  getUiConfig() {
    this.loading = true;
    this.admnSrvc
      .getUIConfigHeaders({
        uiconfig: 'pm-dashboard',
        type: this.configType,
        implementation: 'pm',
      })
      .subscribe({
        next: resp => {
          if (resp && resp['headers']) {
            this.gridData.headers = resp['headers'];
          }
          this.getTopPolicyReview();
        },
        error: err => {
          console.warn(err);
          this.loading = false;
        },
      });
  }

  getTopPolicyReview() {
    const reportParams = {
      take: this.take,
      skip: this.skip,
      search: { unitId: { searchString: this.filterValues['unitId'] } },
      name: this.reportName,
    };

    const selectionVars = [
      'npn',
      'accountType',
      'productType',
      'carrier',
      'productName',
      'userId',
    ];

    selectionVars.map(varName => {
      if (this.filterValues[varName] && this.filterValues[varName].length > 0) {
        reportParams['search'][varName] = {
          selected: this.filterValues[varName],
        };
      }
    });

    if (this.filterValues['start'] && this.filterValues['end']) {
      reportParams['search']['SavedTime'] = {
        start: this.filterValues['start'],
        end: this.filterValues['end'],
      };
    }

    this.dbs.getDynamicReport(reportParams).subscribe({
      next: resp => {
        if (resp && resp['data']) {
          this.gridData.data = resp['data'];
          this.gridData.total = resp['total'];
        }
        this.loading = false;
        this.cdRef.markForCheck();
      },
      error: err => {
        console.warn(err);
        this.loading = false;
      },
    });
  }

  dataStateChange(ev) {
    this.loading = true;
    this.take = ev.take;
    this.skip = ev.skip;
    this.getTopPolicyReview();
  }

  downloadData() {
    this.fileLoading = true;
    const reportParams = {
      take: 10000,
      skip: 0,
      search: { unitId: { searchString: this.filterValues['unitId'] } },
      name: this.reportName,
    };

    const selectionVars = [
      'npn',
      'accountType',
      'productType',
      'carrier',
      'productName',
    ];

    selectionVars.map(varName => {
      if (this.filterValues[varName] && this.filterValues[varName].length > 0) {
        reportParams['search'][varName] = {
          selected: this.filterValues[varName],
        };
      }
    });

    if (this.filterValues['start'] && this.filterValues['end']) {
      reportParams['search']['SavedTime'] = {
        start: this.filterValues['start'],
        end: this.filterValues['end'],
      };
    }

    this.dbs.getDynamicReportXlsx(reportParams).subscribe({
      next: data => {
        if (data) {
          const date = new Date();
          const filename =
            'TopPolicyReviews-' +
            date.getMonth() +
            date.getDay() +
            date.getFullYear() +
            '.xlsx';
          const iosSafari =
            this.pltfrm.IOS ||
            (navigator?.['userAgentData']?.platform === 'macOS' &&
              navigator.maxTouchPoints > 1);

          let fileData;
          const reader = new FileReader();
          reader.readAsDataURL(data);
          reader.onloadend = () => {
            fileData = reader.result;
            saveAs(fileData, filename, {
              proxyURL: `${this.baseUrl}/util/proxy`,
              proxyTarget: '_self',
              forceProxy: iosSafari,
            });
            this.fileLoading = false;
            this.snacky.open(`Download complete.`, 'Close', {
              duration: 4000,
            });
          };
        } else {
          this.fileLoading = false;
        }
      },
      error: () => {
        this.snacky.open(
          `Oops! Something went wrong. Please try again.`,
          'Close',
          {
            duration: 4000,
          }
        );
        this.fileLoading = false;
      },
    });
  }

  handleRowClick(dataItem) {
    if (dataItem?.policyId) {
      window.open(
        `${this.location.prepareExternalUrl('')}book-management/${
          dataItem.policyId
        }`,
        '_blank'
      );
    }
  }
}
